import { useEffect, useRef } from 'react';
import type { CSSProperties, FC } from 'react';
import { Toaster as SonnerToaster } from 'sonner';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { syncPlatformToastStackMeasurements } from './stackMeasurements';
import { PLATFORM_FEEDBACK_TOASTER_ID, PLATFORM_IN_APP_TOASTER_ID } from './toast';
import type { PlatformToastLane } from './types';

interface PlatformToastHostProps {
    lane?: PlatformToastLane;
}

export const PlatformToastHost: FC<PlatformToastHostProps> = ({ lane = 'feedback' }) => {
    const { resolvedThemeName } = useAntDesignTheme();
    const hostRef = useRef<HTMLElement | null>(null);
    const isInApp = lane === 'in-app';

    useEffect(() => {
        const host = hostRef.current;

        if (!host || typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
            return;
        }

        let animationFrame: number | null = null;
        const observedCards = new Set<Element>();
        const settleTimeouts = new Map<number, number>();

        const syncStackMeasurements = () => {
            animationFrame = null;
            syncPlatformToastStackMeasurements(host);
        };

        const scheduleMeasurement = () => {
            if (animationFrame !== null) {
                cancelAnimationFrame(animationFrame);
            }

            animationFrame = requestAnimationFrame(syncStackMeasurements);
        };

        const scheduleMeasurementSequence = () => {
            scheduleMeasurement();

            [120, 420].forEach((delay) => {
                const pendingTimeout = settleTimeouts.get(delay);

                if (pendingTimeout !== undefined) {
                    window.clearTimeout(pendingTimeout);
                }

                const timeout = window.setTimeout(() => {
                    settleTimeouts.delete(delay);
                    scheduleMeasurement();
                }, delay);

                settleTimeouts.set(delay, timeout);
            });
        };

        const resizeObserver = new ResizeObserver(scheduleMeasurement);
        const observeToastCards = () => {
            observedCards.forEach((card) => {
                if (!host.contains(card)) {
                    resizeObserver.unobserve(card);
                    observedCards.delete(card);
                }
            });

            host.querySelectorAll('.platform-toast').forEach((card) => {
                if (!observedCards.has(card)) {
                    observedCards.add(card);
                    resizeObserver.observe(card);
                }
            });
        };
        const mutationObserver = new MutationObserver(() => {
            observeToastCards();
            scheduleMeasurementSequence();
        });

        observeToastCards();
        mutationObserver.observe(host, {
            attributeFilter: [
                'data-actions-visible',
                'data-collapsed',
                'data-expanded',
                'data-index',
                'data-mounted',
                'data-removed',
            ],
            attributes: true,
            childList: true,
            subtree: true,
        });
        scheduleMeasurementSequence();

        return () => {
            if (animationFrame !== null) {
                cancelAnimationFrame(animationFrame);
            }

            settleTimeouts.forEach((timeout) => window.clearTimeout(timeout));
            settleTimeouts.clear();

            mutationObserver.disconnect();
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <SonnerToaster
            className={`platform-toast-host platform-toast-host--${isInApp ? 'in-app' : 'feedback'}`}
            closeButton={false}
            expand={false}
            gap={8}
            id={isInApp ? PLATFORM_IN_APP_TOASTER_ID : PLATFORM_FEEDBACK_TOASTER_ID}
            mobileOffset={isInApp ? { right: '0.75rem', top: '4rem' } : '0.75rem'}
            offset={isInApp ? { right: '1rem', top: '4.25rem' } : '1rem'}
            position={isInApp ? 'top-right' : 'bottom-right'}
            ref={hostRef}
            swipeDirections={isInApp ? ['right'] : undefined}
            style={
                {
                    '--width': isInApp ? 'min(27rem, calc(100vw - 2rem))' : 'min(24rem, calc(100vw - 2rem))',
                } as CSSProperties
            }
            theme={resolvedThemeName === 'dark' ? 'dark' : 'light'}
            toastOptions={{ unstyled: true }}
            visibleToasts={isInApp ? 4 : 3}
        />
    );
};

export const PlatformToastHosts: FC = () => (
    <>
        <PlatformToastHost />
        <PlatformToastHost lane='in-app' />
    </>
);

export default PlatformToastHost;
